import React, { useEffect, useState, useCallback } from "react";
import api, { API } from "@/api";
import { toast } from "sonner";
import { KeyRound, Plus, Download, Trash2, Copy, Loader2, RefreshCw, CheckCircle2 } from "lucide-react";

const TIER_OPTIONS = [
  { id: "studio", label: "Viral Book Studio — $97" },
  { id: "suite", label: "The Viral Book Suite — $497" },
  { id: "vip", label: "Viral Book Suite VIP — $997" },
  { id: "cohort", label: "Evergreen Cohort — $1,997" },
  { id: "dfy", label: "DFY Evergreen — $4,999" },
  { id: "pubglot_only", label: "PubGlot Only (license-only)" },
  { id: "cohort_plan", label: "Cohort 3-month plan" },
  { id: "dfy_plan", label: "DFY 3-month plan" },
];

export default function LicensesPanel() {
  const [licenses, setLicenses] = useState([]);
  const [stats, setStats] = useState({ total: 0, unused: 0, redeemed: 0 });
  const [loading, setLoading] = useState(true);
  const [filterTier, setFilterTier] = useState("");
  const [filterStatus, setFilterStatus] = useState("");

  // Generate form
  const [genTier, setGenTier] = useState("vip");
  const [genQty, setGenQty] = useState(1);
  const [genBusy, setGenBusy] = useState(false);
  const [lastBatch, setLastBatch] = useState(null); // {tier_name, codes:[...]}

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterTier) params.tier_id = filterTier;
      if (filterStatus) params.status_filter = filterStatus;
      const [{ data: rows }, { data: s }] = await Promise.all([
        api.get("/api/admin/licenses", { params }),
        api.get("/api/admin/licenses/stats"),
      ]);
      setLicenses(rows);
      setStats(s);
    } catch (e) {
      toast.error("Could not load licenses");
    } finally {
      setLoading(false);
    }
  }, [filterTier, filterStatus]);

  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    const qty = Math.max(1, Math.min(500, parseInt(genQty, 10) || 1));
    setGenBusy(true);
    try {
      const { data } = await api.post("/api/admin/licenses/generate", { tier_id: genTier, quantity: qty });
      setLastBatch({ tier_name: data.tier_name, codes: data.codes });
      toast.success(`Generated ${data.count} code${data.count === 1 ? "" : "s"} for ${data.tier_name}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Generate failed");
    } finally {
      setGenBusy(false);
    }
  };

  const copyCode = (c) => {
    navigator.clipboard?.writeText(c);
    toast.success(`Copied ${c}`);
  };

  const copyBatch = () => {
    if (!lastBatch?.codes?.length) return;
    navigator.clipboard?.writeText(lastBatch.codes.join("\n"));
    toast.success(`Copied ${lastBatch.codes.length} codes`);
  };

  const downloadBatchTxt = () => {
    if (!lastBatch?.codes?.length) return;
    const blob = new Blob([lastBatch.codes.join("\n")], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `vbs-licenses-${lastBatch.tier_name.replace(/\s+/g, "-").toLowerCase()}-${Date.now()}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportCsv = async () => {
    const token = localStorage.getItem("vbs_token");
    const params = new URLSearchParams();
    if (filterTier) params.set("tier_id", filterTier);
    if (filterStatus) params.set("status_filter", filterStatus);
    const url = `${API}/admin/licenses/export.csv${params.toString() ? "?" + params.toString() : ""}`;
    try {
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const dlUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = dlUrl;
      a.download = "vbs-licenses.csv";
      a.click();
      URL.revokeObjectURL(dlUrl);
    } catch (e) {
      toast.error("CSV export failed");
    }
  };

  const removeCode = async (code) => {
    if (!window.confirm(`Delete license ${code}? This cannot be undone.`)) return;
    try {
      await api.delete(`/api/admin/licenses/${encodeURIComponent(code)}`);
      toast.success("Deleted");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div style={{ marginBottom: 28 }} data-testid="licenses-panel">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10, flexWrap: "wrap", gap: 10 }}>
        <h2 style={{ fontFamily: "var(--font-display)", fontSize: 20, color: "var(--white)", margin: 0, display: "inline-flex", alignItems: "center", gap: 8 }}>
          <KeyRound size={16} style={{ color: "var(--gold)" }} />
          License Codes
        </h2>
        <div style={{ display: "inline-flex", gap: 16, fontSize: 12, color: "var(--mid)" }}>
          <span data-testid="lic-stat-total">Total: <strong style={{ color: "var(--bright)" }}>{stats.total}</strong></span>
          <span data-testid="lic-stat-unused">Unused: <strong style={{ color: "#6dd4a0" }}>{stats.unused}</strong></span>
          <span data-testid="lic-stat-redeemed">Redeemed: <strong style={{ color: "var(--gold)" }}>{stats.redeemed}</strong></span>
        </div>
      </div>

      {/* Generate form */}
      <div className="vbs-card" style={{ padding: 22, marginBottom: 18 }} data-testid="lic-generate-card">
        <div style={{ fontFamily: "var(--font-mono)", fontSize: 10, letterSpacing: 2, color: "var(--gold-dim)", textTransform: "uppercase", marginBottom: 12 }}>
          Generate codes
        </div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
          <label style={{ flex: "1 1 220px", minWidth: 200 }}>
            <div style={{ fontSize: 11, color: "var(--mid)", marginBottom: 4 }}>Plan / Tier</div>
            <select className="vbs-input" value={genTier} onChange={(e) => setGenTier(e.target.value)} data-testid="lic-gen-tier" style={{ width: "100%" }}>
              {TIER_OPTIONS.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
            </select>
          </label>
          <label style={{ width: 130 }}>
            <div style={{ fontSize: 11, color: "var(--mid)", marginBottom: 4 }}>Quantity</div>
            <input
              type="number" min={1} max={500} className="vbs-input"
              value={genQty}
              onChange={(e) => setGenQty(e.target.value)}
              data-testid="lic-gen-qty"
              style={{ width: "100%" }}
            />
          </label>
          <button
            onClick={generate}
            disabled={genBusy}
            className="vbs-btn vbs-btn-gold"
            style={{ padding: "11px 18px" }}
            data-testid="lic-gen-submit"
          >
            {genBusy ? <><Loader2 size={13} className="animate-spin" /> Generating…</> : <><Plus size={13} /> Generate</>}
          </button>
          <button
            onClick={() => { setGenQty(1); }}
            className="vbs-btn vbs-btn-ghost"
            style={{ padding: "11px 14px", fontSize: 11 }}
            title="Reset to single-code mode"
          >
            Single
          </button>
        </div>

        {/* Last-batch preview */}
        {lastBatch && (
          <div style={{ marginTop: 16, padding: 14, borderRadius: 10, border: "1px solid var(--line)", background: "rgba(196,149,42,0.04)" }} data-testid="lic-last-batch">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10, flexWrap: "wrap", gap: 8 }}>
              <div style={{ fontSize: 12, color: "var(--bright)", display: "inline-flex", alignItems: "center", gap: 6 }}>
                <CheckCircle2 size={13} style={{ color: "#6dd4a0" }} /> {lastBatch.codes.length} code{lastBatch.codes.length === 1 ? "" : "s"} for <strong>{lastBatch.tier_name}</strong>
              </div>
              <div style={{ display: "inline-flex", gap: 6 }}>
                <button onClick={copyBatch} className="vbs-btn vbs-btn-ghost" style={{ padding: "5px 10px", fontSize: 11 }} data-testid="lic-batch-copy">
                  <Copy size={11} /> Copy all
                </button>
                <button onClick={downloadBatchTxt} className="vbs-btn vbs-btn-ghost" style={{ padding: "5px 10px", fontSize: 11 }} data-testid="lic-batch-download">
                  <Download size={11} /> Download .txt
                </button>
              </div>
            </div>
            <div style={{
              fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--bright)",
              background: "rgba(0,0,0,0.35)", borderRadius: 6, padding: 10,
              maxHeight: 200, overflow: "auto", whiteSpace: "pre", lineHeight: 1.7,
            }}>
              {lastBatch.codes.join("\n")}
            </div>
          </div>
        )}
      </div>

      {/* Filter bar */}
      <div className="vbs-card" style={{ padding: 0, overflow: "hidden" }}>
        <div style={{ display: "flex", gap: 10, padding: "10px 14px", borderBottom: "1px solid var(--line)", flexWrap: "wrap", alignItems: "center" }}>
          <select className="vbs-input" value={filterTier} onChange={(e) => setFilterTier(e.target.value)} data-testid="lic-filter-tier" style={{ minWidth: 180 }}>
            <option value="">All plans</option>
            {TIER_OPTIONS.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
          </select>
          <select className="vbs-input" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} data-testid="lic-filter-status" style={{ minWidth: 130 }}>
            <option value="">All status</option>
            <option value="unused">Unused</option>
            <option value="redeemed">Redeemed</option>
          </select>
          <button onClick={load} className="vbs-btn vbs-btn-ghost" style={{ padding: "8px 12px", fontSize: 11 }} data-testid="lic-refresh">
            <RefreshCw size={11} /> Refresh
          </button>
          <span style={{ flex: 1 }} />
          <button onClick={exportCsv} className="vbs-btn vbs-btn-ghost" style={{ padding: "8px 12px", fontSize: 11 }} data-testid="lic-export-csv">
            <Download size={11} /> Export CSV
          </button>
        </div>

        <div style={{ overflowX: "auto" }}>
          <table className="vbs-table" data-testid="licenses-table">
            <thead>
              <tr>
                <th>Code</th>
                <th>Plan</th>
                <th>Status</th>
                <th>Created</th>
                <th>Redeemed by</th>
                <th style={{ textAlign: "right" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={6} style={{ textAlign: "center", color: "var(--muted)", padding: 22 }}>Loading…</td></tr>
              )}
              {!loading && licenses.length === 0 && (
                <tr><td colSpan={6} style={{ textAlign: "center", color: "var(--muted)", padding: 22 }} data-testid="lic-empty">No license codes match these filters yet.</td></tr>
              )}
              {licenses.map((l) => (
                <tr key={l.code} data-testid={`lic-row-${l.code}`}>
                  <td style={{ fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--bright)" }}>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                      {l.code}
                      <button onClick={() => copyCode(l.code)} title="Copy" style={{ background: "none", border: "none", color: "var(--muted)", cursor: "pointer", padding: 2 }} data-testid={`lic-copy-${l.code}`}>
                        <Copy size={11} />
                      </button>
                    </span>
                  </td>
                  <td style={{ fontSize: 12, color: "var(--mid)" }}>{l.tier_name || l.tier_id}</td>
                  <td>
                    <span className={"vbs-chip " + (l.status === "unused" ? "ok" : "processing")}>{l.status}</span>
                  </td>
                  <td style={{ fontSize: 11, color: "var(--muted)" }}>{(l.created_at || "").slice(0, 10) || "—"}</td>
                  <td style={{ fontSize: 11, color: "var(--mid)" }}>{l.redeemed_by_email || "—"}</td>
                  <td style={{ textAlign: "right" }}>
                    {l.status === "unused" ? (
                      <button
                        onClick={() => removeCode(l.code)}
                        className="vbs-btn vbs-btn-ghost"
                        style={{ padding: "5px 9px", fontSize: 11 }}
                        data-testid={`lic-delete-${l.code}`}
                        title="Delete unused code"
                      >
                        <Trash2 size={12} />
                      </button>
                    ) : (
                      <span style={{ fontSize: 10, color: "var(--muted)" }}>locked</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
